import { SelectOption } from '@globaleris/ui.library'
import { useCallback, useEffect, useState } from 'react'
import {
  EntityForDomain,
  getEntityForDomain,
  TaskModel,
  TaskTypeModel
} from '../../../api/Argentia/endpoints/tasks/model/tasks'
import { EnumDominioTarea } from '../../../api/oData/enums/enumDominioTarea'
import { formatDateOnlyDate } from '../../../helpers/formatDateOnly'
import { ObjectionsModel, ObjectionTableModel } from '../../invoicesPage/invoicesPage.Model'
import { getTaskModelRelatedEntityValue } from '../task.helpers'
import { mapAtlasUsersToSelectOptionArray, mapUsersToSelectOptionArray } from '../tasks.mapper'
import { GlobalerisClientModel } from '../tasks.model'
import {
  fetchAtlasUsers,
  fetchMeUsers,
  fetchObjectionsByTaskDomain,
  fetchTaskTypes,
  fetchTaskWithObjectionsById
} from '../tasks.services'

export const useFetchGlobalerisUsers = (domain: string, setError: (errorMsg: string) => void) => {
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [globalerisClient, setGlobalerisClient] = useState<GlobalerisClientModel>()
  const [globClientUsersOptions, setGlobClientUsersOptions] = useState<SelectOption[]>([])

  useEffect(() => {
    const getUsers = async () => {
      setIsFetching(true)
      try {
        const response = await fetchMeUsers()
        if (response.success && response.data) {
          setGlobClientUsersOptions(mapUsersToSelectOptionArray(response.data))
        } else {
          setError('tasks:fetchErrors.globalerisClientUsers')
        }
      } finally {
        setIsFetching(false)
      }
    }
    getUsers()
  }, [])

  return { globClientUsersOptions, globalerisClient, isFetching }
}

export const useFetchAtlasUsers = (
  setError: (errorMsg: string) => void,
  fetchAtlasUsersTrigger: boolean = true
) => {
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [atlasUsersOptions, setAtlasUsersOptions] = useState<SelectOption[]>([])

  useEffect(() => {
    if (!fetchAtlasUsersTrigger) return

    setIsFetching(true)
    fetchAtlasUsers()
      .then(response => {
        if (response.success && response.data) {
          setAtlasUsersOptions(mapAtlasUsersToSelectOptionArray(response.data))
        } else {
          setError('tasks:fetchErrors.atlasUsers')
        }
      })
      .finally(() => {
        setIsFetching(false)
      })
  }, [fetchAtlasUsersTrigger])

  return { atlasUsersOptions, isFetching }
}

export const useGetTaskDetails = (
  taskId: number,
  taskDomain: EnumDominioTarea,
  setError: (errorMsg: string) => void
) => {
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [taskDetails, setTaskDetails] = useState<TaskModel | undefined>()
  const [taskRelatedEntity, setTaskRelatedEntity] = useState<
    EntityForDomain<EnumDominioTarea> | undefined
  >()
  const [objections, setObjections] = useState<ObjectionsModel[] | undefined>()
  const [relatedEntityCodeOptions, setRelatedEntityCodeOptions] = useState<SelectOption[]>([])

  useEffect(() => {
    const getTaskDetails = (taskId: number, taskDomain: EnumDominioTarea) => {
      setIsFetching(true)
      fetchTaskWithObjectionsById(taskId, taskDomain)
        .then(response => {
          if (response.success && response.data) {
            setTaskDetails({
              ...response.data.task,
              creationDate: formatDateOnlyDate(response.data.task.creationDate)!,
              closingDate: formatDateOnlyDate(response.data.task.closingDate)
            })
            setObjections(response.data.objections)
            const relatedEntityValue = getTaskModelRelatedEntityValue(response.data.task)
            // handle relatedEntityCode -> undefined when relatedEntity has been replaced (e.g. Factura se ha regenerado)
            // selectOption is introduced to handle isLoading spinner
            if (
              relatedEntityValue &&
              relatedEntityValue.length > 0 &&
              relatedEntityValue[0] !== undefined
            ) {
              setRelatedEntityCodeOptions([
                {
                  value: relatedEntityValue[0],
                  label: relatedEntityValue[0]
                }
              ])
              setTaskRelatedEntity(getEntityForDomain(taskDomain, response.data.task))
            } else {
              setError('tasks:fetchErrors.relatedEntityCodeNull')
            }
          } else {
            setError('tasks:fetchErrors.taskEdit')
          }
        })
        .finally(() => {
          setIsFetching(false)
        })
    }

    //get task details to fill relatedEntityCode
    getTaskDetails(taskId, taskDomain)

    //eslint-disable-next-line
  }, [taskId])

  return { isFetching, relatedEntityCodeOptions, taskDetails, objections, taskRelatedEntity }
}

export const useGetTaskTypes = (
  setError: (errorMsg: string) => void,
  fetchTaskTrigger: boolean = true
) => {
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [taskTypes, setTaskTypes] = useState<TaskTypeModel[]>([])

  const getTaskTypes = useCallback((setError: (errorMsg: string) => void) => {
    setIsFetching(true)
    fetchTaskTypes()
      .then(response => {
        if (response.success && response.data) {
          setTaskTypes(response.data)
        } else {
          setError('tasks:fetchErrors.taskTypes')
        }
      })
      .finally(() => {
        setIsFetching(false)
      })
  }, [])

  useEffect(() => {
    fetchTaskTrigger && getTaskTypes(setError)
  }, [fetchTaskTrigger])

  return { isFetching, taskTypes, getTaskTypes }
}

export const useGetObjectionsDescriptionList = (
  taskDomain: EnumDominioTarea,
  setError: (errorMsg: string) => void
) => {
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [objDescriptionList, setObjDescriptionList] = useState<ObjectionTableModel[]>([])

  useEffect(() => {
    const getObjectionsDescription = () => {
      setIsFetching(true)
      fetchObjectionsByTaskDomain(taskDomain)
        .then(response => {
          if (response.success && response.data) {
            setObjDescriptionList(response.data)
          } else {
            setError('tasks:fetchErrors.objectionsDescription')
          }
        })
        .finally(() => {
          setIsFetching(false)
        })
    }

    getObjectionsDescription()
  }, [])

  return { isFetching, objDescriptionList }
}
